/* Theme behaviour. No framework and no build step: the whole file is one small
   module, so there is nothing to hydrate before the page is interactive.

   The cart is the only stateful part. Every mutation asks for the drawer in
   the same request that changes the cart and paints from that one response, so
   the count, the lines and the subtotal can never be patched independently and
   drift apart. refresh() is the fallback for the paths with no mutation to ride
   along with, such as opening the drawer from the header. */

const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

/* Focus trapping. A dialog that leaks focus to the page behind it is unusable
   with a keyboard and fails an audit, and it is three lines to do properly. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

let lastFocused = null;

/* Locking the page behind an open panel.
   `overflow: hidden` on the body is the usual answer and iOS Safari ignores it
   outright, so a touch drag anywhere over the drawer kept scrolling the store
   underneath and the product page showed through. Pinning the body with
   `position: fixed` is the only lock iOS honours. That drops the page to the
   top, so the scroll offset is parked in `lockedAt` and restored on close.
   The depth counter matters because the nav panel and the cart drawer share
   these two functions: without it, closing one would unlock the page while the
   other is still open. */
let lockedAt = 0;
let lockDepth = 0;

function lockScroll() {
  if (lockDepth++ > 0) return;
  lockedAt = window.scrollY;
  const { style } = document.body;
  style.position = 'fixed';
  style.top = `-${lockedAt}px`;
  style.left = '0';
  style.right = '0';
  style.width = '100%';
  style.overflow = 'hidden';
}

function unlockScroll() {
  lockDepth = Math.max(0, lockDepth - 1);
  if (lockDepth > 0) return;
  const { style } = document.body;
  style.position = '';
  style.top = '';
  style.left = '';
  style.right = '';
  style.width = '';
  style.overflow = '';
  window.scrollTo(0, lockedAt);
}

function openPanel(panel) {
  /* Opening an already-open panel must not lock the page a second time.
     The scroll lock is a depth counter, so a second lock needs a second
     close to release, and there is never a second close: the page stays
     pinned with a negative top, which reads as a frozen screen scrolled
     away from whatever the customer was looking at. Adding to the cart
     opens the drawer twice, once on the markup that came back with the add
     and once after the cart settles, which is exactly how this happened. */
  if (!panel.hidden) return;

  lastFocused = document.activeElement;
  panel.hidden = false;
  lockScroll();
  const first = $(FOCUSABLE, panel);
  if (first) first.focus();
}

function closePanel(panel) {
  if (panel.hidden) return;
  panel.hidden = true;
  unlockScroll();
  if (lastFocused) lastFocused.focus();
}

function trap(event, panel) {
  if (event.key === 'Escape') {
    closePanel(panel);
    return;
  }
  if (event.key !== 'Tab') return;

  const nodes = $$(FOCUSABLE, panel).filter((n) => n.offsetParent !== null);
  if (nodes.length === 0) return;

  const first = nodes[0];
  const last = nodes[nodes.length - 1];

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

/* Mobile navigation */
function initNav() {
  const panel = $('[data-nav-panel]');
  const openBtn = $('[data-nav-open]');
  if (!panel || !openBtn) return;

  /* The header uses backdrop-filter, which makes it the containing block for
     fixed descendants, so the panel would size to the header bar instead of
     the viewport. Moving it to body restores real full-screen fixed. */
  document.body.appendChild(panel);

  openBtn.addEventListener('click', () => {
    openPanel(panel);
    openBtn.setAttribute('aria-expanded', 'true');
  });

  panel.addEventListener('click', (e) => {
    if (e.target.closest('[data-nav-close]')) {
      closePanel(panel);
      openBtn.setAttribute('aria-expanded', 'false');
    }
  });

  document.addEventListener('keydown', (e) => {
    if (!panel.hidden) trap(e, panel);
  });
}

/* Cart */
/* The drawer lives in the footer section group, so its id is generated rather
   than fixed. It publishes that id on the element and every caller reads it,
   which is what keeps the Section Rendering API request addressable. */
function drawerSectionId() {
  const el = $('[data-cart-drawer]');
  return el ? el.dataset.sectionId : null;
}

/* Swap in drawer markup that has already been fetched.

   Every cart mutation can return the rendered drawer in the same response,
   so the markup is nearly always in hand by the time anything needs painting.
   Fetching it again is a second round trip for a document we already have,
   and it is what made every tap on the quantity buttons feel slow. */
/* The cart as the server last rendered it, lifted out of the drawer markup.
   Every mutation that asks for the section back refreshes it, so the paths
   that used to fetch /cart.js to learn the cart's state read this instead. */
let lastCart = null;

/* A line's key is not stable: Shopify rebuilds it whenever the automatic
   discount lands on a different line, which any add or removal can cause.
   The `_i` property is written once per line and never changes, so it is
   how a line is found again after the cart has moved. */
function lineBySep(cart, sep, fallbackKey) {
  if (!cart || !cart.items) return null;
  return (sep && cart.items.find((i) => i.properties && i.properties._i === sep))
    || cart.items.find((i) => i.key === fallbackKey)
    || null;
}

function cartFromDoc(doc) {
  const el = $('[data-cart-json]', doc);
  if (!el) return null;
  try { return JSON.parse(el.textContent); } catch (err) { return null; }
}

function paintDrawer(markup) {
  if (!markup) return false;
  const parsed = new DOMParser().parseFromString(markup, 'text/html');
  const next = $('[data-cart-drawer]', parsed);
  const current = $('[data-cart-drawer]');
  if (!next || !current) return false;

  const carried = cartFromDoc(parsed);
  if (carried) lastCart = carried;

  /* Swap what is inside the panel, never the panel itself.
     The panel carries the slide-in animation and the scrim carries the fade,
     and an animation restarts whenever its element is re-mounted. Replacing
     the whole drawer therefore replayed the open animation on every update:
     on a phone the panel visibly flew in from the right again, at its own
     width, for a couple of frames after each tap on the quantity buttons.
     Keeping both elements means those animations run when the drawer opens
     and at no other time. */
  const nextPanel = $('[data-cart-panel]', parsed);
  const currentPanel = $('[data-cart-panel]');
  if (nextPanel && currentPanel) {
    currentPanel.innerHTML = nextPanel.innerHTML;
    return true;
  }

  const wasOpen = !current.hidden;
  current.innerHTML = next.innerHTML;
  current.hidden = !wasOpen;
  return true;
}

/* Fetch the drawer on its own. Only for the paths that have no mutation to
   ride along with, such as opening the drawer from the header. */
async function refresh() {
  const id = drawerSectionId();
  if (!id) return;

  const url = `${window.location.pathname}?sections=${encodeURIComponent(id)}`;

  /* No Accept header here on purpose. On a product URL, Accept:
     application/json makes Shopify return the product resource as JSON and
     ignore the Section Rendering API entirely, which silently yields an empty
     drawer. The default Accept is what routes this to the section renderer. */
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Section refresh failed: ${res.status}`);

  const data = await res.json();
  paintDrawer(data[id]);
}

/* The cart and the drawer markup in a single request.

   /cart.js cannot render a section, so anything needing both state and markup
   used to spend two round trips on them. /cart/update.js with nothing to
   update is a read of the same cart, and it does honour the sections
   parameter, so one request answers both. */
async function cartAndDrawer() {
  const id = drawerSectionId();
  const res = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ updates: {}, sections: id || undefined }),
  });
  if (!res.ok) throw new Error(`Cart read failed: ${res.status}`);
  const cart = await res.json();
  return { cart, markup: (cart.sections && id && cart.sections[id]) || null };
}

/* The free gifts travel with the offer: present exactly when the bundle is in
   the cart, gone the moment it is not. Runs after every cart mutation, so
   no path can strand a free line or lose one.

   Switching the gifts off does not just stop adding them. A cart opened while
   they were on still holds them, and one of them is a gift card, which is real
   money. So the ids stay listed in the section while the switch is off, purely
   so this can take those lines back out of a cart that already has them. */
async function reconcileGifts(known) {
  const el = $('[data-cart-drawer]');
  if (!el) return;
  const giftIds = (el.dataset.giftVariants || '').split(',').map(Number).filter(Boolean);
  const label = el.dataset.freeProp;
  const giftsOn = el.dataset.giftsOn !== 'false';
  if (!giftIds.length || !label) return;

  /* Callers hand over the cart the mutation just returned. Fetching another
     copy of it was a whole round trip on every single cart interaction, spent
     to learn something the caller already knew. With the gifts switched off
     and no stale gift lines to sweep, this now costs no requests at all. */
  const cart = known || await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
  const hasBundle = giftsOn && cart.items.some((i) => i.properties && i.properties[label]);
  const giftLines = cart.items.filter((i) => giftIds.includes(i.variant_id));
  if (!hasBundle && !giftLines.length) return false;

  if (hasBundle) {
    const missing = giftIds.filter((id) => !giftLines.some((l) => l.variant_id === id));
    if (missing.length) {
      await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: missing.map((id) => ({ id, quantity: 1, properties: { _gift: '1' } })) }),
      });
      return true;
    }
    // a gift can only ever be quantity one
    for (const l of giftLines) {
      if (l.quantity > 1) {
        await fetch(`${window.Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: l.key, quantity: 1 }),
        });
        return true;
      }
    }
  } else if (giftLines.length) {
    for (const l of giftLines) {
      await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: l.key, quantity: 0 }),
      });
    }
    return true;
  }
  return false;
}

/* Quantity changes are the fastest way to make a cart feel broken: tap twice
   and two requests race, and whichever lands last paints, which can be the
   older one. Every change takes a ticket, and a response only paints if its
   ticket is still the newest. */
let cartTicket = 0;

/* One cart mutation at a time.

   The ticket above stops an older response painting over a newer one, but it
   cannot stop the older one being processed last. Two requests in flight are
   ordered by the server, not by the order the thumb sent them, so a fast
   double tap could leave the drawer showing a row the cart no longer held.
   Queueing the mutations removes the race rather than papering over it, and
   costs nothing: they were always going to be applied one after another. */
let cartQueue = Promise.resolve();
let cartPending = 0;
/* True when nothing else is waiting behind the operation running now.

   The offer only needs reconciling once a burst is over. Five fast taps used
   to run five converge passes, each spending requests to reach a state the very
   next tap invalidated, and the cart took seconds to settle because all of that
   work is serialised. Only the last operation in the queue reconciles; the ones
   before it just apply their own change and paint. */
function lastInQueue() { return cartPending <= 1; }

function inTurn(fn) {
  cartPending += 1;
  const run = cartQueue.then(fn, fn);
  const done = () => { cartPending -= 1; };
  cartQueue = run.then(done, done);
  return run;
}

function changeLine(key, quantity, sep) {
  return inTurn(() => changeLineNow(key, quantity, sep));
}

/* Removing the line that carries the free marker is not "take the free one
   away". The discount zeroes whichever line it likes, so the row the
   customer sees as paid can be the marked one, and removing it as a bonus
   made the top-up put a free garment straight back: the tap did nothing.
   The customer removed a garment; the marker moves to one that stays, and
   the count is settled from there. Returns null when this is not that case. */
async function removeMarkedLine(key, sep, id) {
  const host = lineBySep(lastCart, sep, key);
  if (!host || !host.properties || !host.properties._free) return null;
  const chosen = lastCart.items.filter((i) => i.product_id === host.product_id
    && i.key !== host.key && !(i.properties && (i.properties._free || i.properties._gift)));
  if (!chosen.length) return null;
  const heir = chosen.find((i) => i.final_line_price === 0) || chosen[chosen.length - 1];

  const res = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ id: host.key, quantity: 0, sections: id || undefined }),
  });
  if (!res.ok) return null;
  let cart = await res.json();
  let markup = cart.sections && id ? cart.sections[id] : null;

  /* The removal may have moved the discount and renamed the heir. */
  const heirNow = lineBySep(cart, heir.properties && heir.properties._i, heir.key);
  if (heirNow) {
    const props = Object.assign({}, heirNow.properties, { _free: '1', _picked: '1' });
    const marked = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: heirNow.key, properties: props, sections: id || undefined }),
    });
    if (marked.ok) {
      cart = await marked.json();
      markup = cart.sections && id ? cart.sections[id] : null;
    }
  }
  return { cart, markup };
}

async function changeLineNow(key, quantity, sep, retried) {
  const ticket = ++cartTicket;
  const id = drawerSectionId();

  if (quantity === 0 && !retried) {
    const moved = await removeMarkedLine(key, sep, id);
    if (moved) {
      let cart = moved.cart;
      if (ticket === cartTicket) {
        if (!paintDrawer(moved.markup)) await refresh();
        syncCount(cart.item_count);
      }
      const topped = lastInQueue() ? await topUpFreeThird(cart) : null;
      if (topped) cart = topped.cart;
      if (ticket === cartTicket) syncCount(cart.item_count);
      return cart;
    }
  }

  /* Ask for the drawer in the same request that changes the cart. The old
     path changed the cart, fetched the cart again to reconcile gifts, then
     fetched the section: three round trips for one tap. This is one. */
  const res = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ id: key, quantity, sections: id || undefined }),
  });
  /* A line the cart no longer has is not a failure. Tapping remove twice on
     the same row sends the same key twice, and the second one arrives after
     the row is gone: repaint from the truth rather than throwing and putting
     a number back that the customer never asked for. */
  if (res.status === 404 || res.status === 400) {
    const fresh = await cartAndDrawer();
    /* The key the row carried may simply be stale: the discount moved and
       Shopify renamed the line. Find it again by its marker and go once more. */
    const again = retried ? null : lineBySep(fresh.cart, sep, null);
    if (again && again.key !== key) {
      cartTicket -= 1;
      return changeLineNow(again.key, quantity, sep, true);
    }
    if (ticket === cartTicket) {
      if (!paintDrawer(fresh.markup)) await refresh();
      syncCount(fresh.cart.item_count);
    }
    return fresh.cart;
  }
  if (!res.ok) throw new Error(`Cart update failed: ${res.status}`);

  let cart = await res.json();
  let markup = cart.sections && id ? cart.sections[id] : null;

  /* On screen now, off the response that made the change. The top-up below
     may repaint once more a moment later, but the row the customer touched
     should not wait for it. */
  if (ticket === cartTicket) {
    if (!paintDrawer(markup)) await refresh();
    syncCount(cart.item_count);
  }

  const topped = lastInQueue() ? await topUpFreeThird(cart) : null;
  if (topped) {
    cart = topped.cart;
    markup = topped.markup;
  }

  /* Gifts only cost a request when there is actually something to change,
     and that invalidates the markup this response carried. */
  if (await reconcileGifts(cart)) {
    const again = await fetch(`${window.Shopify.routes.root}cart.js`);
    cart = await again.json();
    markup = null;
  }

  if (ticket !== cartTicket) return cart;

  if (topped || !markup) {
    if (!paintDrawer(markup)) await refresh();
  }
  syncCount(cart.item_count);
  return cart;
}

/* Top the cart up so the offer can apply.

   One in every three of the offer item is free, so a cart holding a remainder
   of two is one short of the trio the discount needs. This adds that one,
   matching the variant already in the cart so nothing is chosen for the
   customer that they did not already pick; the cart then invites them to
   change its colour and size.

   Nothing here makes anything free. That is the discount's job, and the switch
   in the section exists so this cannot run before the discount is live and
   silently add a garment the customer is then charged for. */
/* Keeps the free garments in step with what the cart earns: adds one when a
   customer crosses the threshold, and takes one back when they drop below it.

   CALLERS, and every path that can change how many garments are in the cart
   must be one of them. Missing a path is not a visible error, it is a free
   garment that silently never appears, or one that silently stays:
     - changeLine, the minus button and any quantity change
     - addAnother, the plus button
     - the cart:changed event, which is every add from a product page
     - opening the drawer, which is not a change but can follow one
   The plus button was missed once, and reaching two that way gave no free
   garment at all. */
/* One adjustment towards the offer, or null when every product in the cart
   already holds exactly what it earns.

   Grouped by product, so the offer belongs to the range rather than to one
   item: two tees earn a free tee, two pairs of socks earn a free pair, and
   neither counts towards the other. Mixing one of each earns nothing, which is
   what "two of a thing gets you a third" means.

   A line the cart is not charging for is skipped. The handbook and the gift
   card are both zero, and a gift card breeding gift cards would be issuing
   store value out of a theme file. */
/* Shopify runs buy 2 get 1 and buy 3 get 2 on every product and applies
   whichever frees more, so this is the larger of the two. The buy box's
   freeFor holds the same rule. */
function freeOwed(paid) {
  return Math.max(Math.floor(paid / 2), 2 * Math.floor(paid / 3));
}

function nextFreeStep(cart, label, asLine) {
  const byProduct = new Map();

  for (const line of cart.items) {
    if (line.properties && line.properties._gift) continue;
    /* original_price is the figure before any discount, so the free line still
       counts as a garment here even once the discount has priced it at zero.
       The handbook and the gift card are zero before any discount, and that is
       what this is filtering out. */
    const charged = line.original_price || line.price || 0;
    if (!(charged > 0)) continue;
    if (!byProduct.has(line.product_id)) byProduct.set(line.product_id, []);
    byProduct.get(line.product_id).push(line);
  }

  for (const lines of byProduct.values()) {
    if (asLine) {
      /* A line still carrying the property-style free garment comes from the
         older mode: a cart started before the switch, or a buy box that could
         not see the drawer. Left alone it draws a second free row and reaches
         checkout as a property, so it is cleared before anything is counted. */
      const legacy = lines.find((i) => i.properties && i.properties[label]);
      if (legacy) {
        const properties = Object.assign({}, legacy.properties);
        delete properties[label];
        delete properties._picked;
        return { op: 'change', key: legacy.key, quantity: legacy.quantity, properties };
      }

      /* The free garment is a real line the automatic discount prices at zero.
         Count what the customer chose separately from what the offer added, so
         removing one they chose takes the bonus back with it instead of the
         two fighting each other. */
      const bonus = lines.filter((i) => i.properties && i.properties._free);
      const chosen = lines.filter((i) => !(i.properties && i.properties._free));
      const chosenUnits = chosen.reduce((n, i) => n + i.quantity, 0);
      const bonusUnits = bonus.reduce((n, i) => n + i.quantity, 0);
      const owed = freeOwed(chosenUnits);
      if (bonusUnits === owed) continue;

      if (bonusUnits > owed) {
        const host = bonus[bonus.length - 1];
        return { op: 'change', key: host.key, quantity: Math.max(0, host.quantity - 1) };
      }

      /* Match a garment the customer already chose, so nothing is picked for
         them that they did not already want. The cart then asks them to set
         it, and the line's own pencil writes it back. */
      const host = chosen[chosen.length - 1];
      if (!host) continue;
      /* Carry the full price across from the garment this one copies, so the
         line the cart adds by itself quotes the same figure at checkout as the
         ones the customer picked. */
      const props = { _free: '1', _i: separator() };
      const rrp = host.properties && host.properties['Full price'];
      if (rrp) props['Full price'] = rrp;
      return { op: 'add', id: host.variant_id, properties: props };
    }

    const paid = lines.reduce((n, i) => n + i.quantity, 0);
    const owed = freeOwed(paid);
    const holders = lines.filter((i) => i.properties && i.properties[label]);
    if (holders.length === owed) continue;

    /* Take one back. Removing a garment changes what the offer owes just as
       much as adding one does, and this only ever added: drop from two paid to
       one and the free garment stayed, so the cart showed one paid item and a
       free one beside it. */
    if (holders.length > owed) {
      const host = holders[holders.length - 1];
      const properties = Object.assign({}, host.properties);
      delete properties[label];
      delete properties._picked;
      return { op: 'change', key: host.key, quantity: host.quantity, properties };
    }

    /* Default the free garment to one the customer already chose, so nothing
       is picked for them that they did not already want.

       The spec is every option value in the product's own order, which is
       "Stealth, L" on a garment with colours and "M" on one sized only. Read
       the options rather than splitting the variant title: a colourway is
       itself named "Black / Blue", the same separator the title uses, so
       splitting "Black / Blue / M" gives three pieces and the free garment
       ends up recorded as a colour that does not exist. */
    const host = lines.find((i) => !(i.properties && i.properties[label]));
    if (!host) continue;   // every line already carries one; cannot hold more
    const spec = (host.options_with_values || []).map((o) => o.value).join(', ');
    if (!spec) continue;
    const properties = Object.assign({}, host.properties || {});
    properties[label] = spec;
    return { op: 'change', key: host.key, quantity: host.quantity, properties };
  }

  return null;
}

async function topUpFreeThird(cart) {
  const el = $('[data-cart-drawer]');
  if (!el || el.dataset.freeThird === 'false') return null;

  const label = el.dataset.freeProp;
  if (!label) return null;

  /* Off by default, and it must stay off until the automatic Buy 2 get 1 free
     discounts exist and are active. With it on the free garment is a real line
     the discount prices at zero, so the order, the packing slip and the
     confirmation email all show it. Turned on without the discounts, the
     customer is charged for it. */
  const asLine = el.dataset.freeLine === 'true';

  /* Ask for the drawer in the same request that moves the free garment.
     Every caller paints straight afterwards, and fetching the cart and then
     the section separately was two extra round trips on the single tap that
     matters most: the one that earns the free garment. */
  const section = drawerSectionId();

  /* Converge, rather than moving the count by one and stopping.

     A cart four garments deep earns two free ones, and this used to hand over
     one. Every caller runs it after a single change, so one step was usually
     enough and the shortfall only showed when several changes landed together.
     Each pass re-reads the cart the previous one returned, so the loop sees its
     own work. With several products in the cart each owing its own free
     garment, the loop is doing real work for longer, hence the headroom on the
     guard. It is a backstop against a cart that cannot settle, never a normal
     exit. */
  let latest = cart;
  let markup = null;
  let moved = false;

  for (let guard = 0; guard < 24; guard += 1) {
    const step = nextFreeStep(latest, label, asLine);
    if (!step) break;

    if (step.op === 'add') {
      /* /cart/add.js answers with the line it added, never the whole cart,
         but the drawer markup it can return carries the cart the server
         rendered it from, so one request answers both. */
      const added = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: step.id, quantity: 1, properties: step.properties }],
          sections: section || undefined,
        }),
      });
      if (!added.ok) break;
      const body = await added.json();
      markup = (body.sections && section && body.sections[section]) || null;
      const carried = markup ? cartFromDoc(new DOMParser().parseFromString(markup, 'text/html')) : null;
      if (carried) {
        latest = carried;
      } else {
        const read = await cartAndDrawer();
        latest = read.cart;
        markup = read.markup;
      }
      /* Each step goes on screen as it lands. The queue keeps mutations in
         order, so nothing older can paint over this. */
      paintDrawer(markup);
      moved = true;
      continue;
    }

    const res = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: step.key, quantity: step.quantity, properties: step.properties,
        sections: section || undefined,
      }),
    });
    if (!res.ok) break;
    latest = await res.json();
    markup = (latest.sections && section && latest.sections[section]) || null;
    paintDrawer(markup);
    moved = true;
  }

  return moved ? { cart: latest, markup } : null;
}

/* The free garment's colour and size. It lives in a line item property rather
   than a variant, and /cart/change.js replaces a line's whole property set
   instead of merging, so the existing ones are sent back with it. */
function initFreePicker() {
  document.addEventListener('click', async (e) => {
    const pencil = e.target.closest('[data-free-edit]');
    if (pencil) {
      const panel = pencil.closest('.ln__main').querySelector('[data-free-editor]');
      if (!panel) return;
      const open = panel.hidden;
      panel.hidden = !open;
      pencil.setAttribute('aria-expanded', open ? 'true' : 'false');
      return;
    }

    const swatch = e.target.closest('[data-fp-colour]');
    const size = e.target.closest('[data-fp-size]');
    if (!swatch && !size) return;

    const editor = (swatch || size).closest('[data-free-editor]');
    const data = JSON.parse($('[data-fp-data]', editor).textContent);

    /* A product sized only, such as the socks, has no colour row at all. Every
       lookup below asks the payload whether the option exists rather than
       assuming a garment is always a colour and a size. */
    const colour = data.hasColour
      ? (swatch ? swatch.dataset.fpColour : pickOn(editor, '[data-fp-colour]', 'fpColour', data.colour))
      : null;
    const wanted = data.hasSize
      ? (size ? size.dataset.fpSize : pickOn(editor, '[data-fp-size]', 'fpSize', data.size))
      : null;
    const matches = (v) => (!data.hasColour || v.colour === colour) && (!data.hasSize || v.size === wanted);

    $$('[data-fp-colour]', editor).forEach((b) => {
      const on = b.dataset.fpColour === colour;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    const name = $('[data-fp-colour-name]', editor);
    if (name) name.textContent = colour;
    $$('[data-fp-size]', editor).forEach((b) => {
      const on = b.dataset.fpSize === wanted;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-checked', on ? 'true' : 'false');
      const v = data.variants.find((x) => (!data.hasColour || x.colour === colour) && x.size === b.dataset.fpSize);
      b.classList.toggle('is-out', !v || !v.available);
    });

    const match = data.variants.find(matches);
    if (!match || !match.available) return;
    /* Compare the spec, not the pair. It is the string the property actually
       holds, so it is the same test whatever options the product has. */
    if (match.spec === data.spec) return;

    /* Same queue as every other cart mutation. Setting the free garment's
       colour while a quantity change is still in flight is exactly the race
       the queue exists to remove. */
    await inTurn(async () => {
      const ticket = ++cartTicket;
      const id = drawerSectionId();
      const properties = Object.assign({}, data.properties);
      properties[data.label] = match.spec;
      // Records that the customer has set it, so the repaint renders the panel
      // shut and the prompt gone rather than nagging about a done job.
      properties._picked = '1';

      editor.classList.add('is-busy');
      const res = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: data.key, properties, sections: id || undefined }),
      });
      if (!res.ok) { editor.classList.remove('is-busy'); return; }
      const cart = await res.json();
      if (ticket !== cartTicket) return;
      if (!paintDrawer(cart.sections && id ? cart.sections[id] : null)) await refresh();
    });
  });
}

function pickOn(editor, selector, key, fallback) {
  const on = $$(selector, editor).find((b) => b.classList.contains('is-on'));
  return on ? on.dataset[key] : fallback;
}

/* Every garment is its own line, even when two are the same colour and size.

   Shopify merges an add into an existing line when the variant and the
   properties both match, which would collapse two identical garments into one
   line of quantity two and leave only one of them editable. A marker property
   keeps them apart. It is underscored, so it is machinery and the customer
   never sees it, and the order still shows the right number of garments. */
let lineSeq = 0;
function separator() {
  lineSeq += 1;
  return `${Date.now()}-${lineSeq}`;
}

/* Swap the variant on a cart line.

   The cart API keys a line to its variant, so there is no "change variant"
   call: the new one is added and the old one removed. The add carries the old
   line's properties over, so a garment that was carrying the offer's free pick
   does not lose it by having its colour changed.

   The swapped line moves to the end of the cart, because an add always
   appends. That is visible and it is the price of doing this at all without
   rebuilding the whole cart. */
function swapVariant(editor, nextId) {
  return inTurn(() => swapVariantNow(editor, nextId));
}

async function swapVariantNow(editor, nextId) {
  const data = JSON.parse($('[data-le-data]', editor).textContent);
  const ticket = ++cartTicket;
  const id = drawerSectionId();
  const note = $('[data-le-note]', editor);
  const properties = Object.assign({}, data.properties || {});
  properties._i = separator();
  /* Setting a colour or size on the free garment answers the drawer's
     question, so the marker that asks it is closed on the way through. */
  if (properties._free) properties._picked = '1';

  editor.classList.add('is-busy');
  try {
    const added = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: nextId, quantity: data.quantity, properties }], sections: id || undefined }),
    });
    if (!added.ok) throw new Error(`Variant swap failed: ${added.status}`);
    const addedBody = await added.json();

    /* Adding a line can move the discount, and when the discount moves
       Shopify renames the lines it touched. The old line is found again by
       its marker in the cart this add rendered, never by the key the editor
       was opened with; that key removed nothing and left both garments. */
    const addedMarkup = (addedBody.sections && id && addedBody.sections[id]) || null;
    let now = addedMarkup ? cartFromDoc(new DOMParser().parseFromString(addedMarkup, 'text/html')) : null;
    if (!now) now = (await cartAndDrawer()).cart;
    const oldSep = data.properties && data.properties._i;
    const old = lineBySep(now, oldSep, data.key);
    let cart = now;
    if (old) {
      const res = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: old.key, quantity: 0, sections: id || undefined }),
      });
      if (!res.ok) throw new Error(`Variant swap failed: ${res.status}`);
      cart = await res.json();
    }
    if (ticket !== cartTicket) return;

    if (!paintDrawer(cart.sections && id ? cart.sections[id] : null)) await refresh();
    syncCount(cart.item_count);

    /* The row the customer edited may not be the one carrying the marker:
       the discount zeroes its own pick of line, and the prompt sits on that
       row. Whichever line of this product still asks, it has been answered. */
    const asking = cart.items.find((i) => i.product_id === data.productId
      && i.properties && i.properties._free && !i.properties._picked);
    if (asking) {
      const props = Object.assign({}, asking.properties, { _picked: '1' });
      const closed = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: asking.key, properties: props, sections: id || undefined }),
      });
      if (closed.ok) {
        cart = await closed.json();
        if (ticket !== cartTicket) return;
        paintDrawer(cart.sections && id ? cart.sections[id] : null);
      }
    }
  } catch (err) {
    editor.classList.remove('is-busy');
    if (note) { note.textContent = 'Could not change that. Please try again.'; note.hidden = false; }
    console.error(err);
  }
}

/* One more of the same garment, as its own line rather than a quantity.
   Two identical rows each carry their own pencil, which is the whole point:
   the customer adds a second and then re-specs it. */
function addAnother(line) {
  return inTurn(() => addAnotherNow(line));
}

async function addAnotherNow(line) {
  const ticket = ++cartTicket;
  const id = drawerSectionId();
  const variantId = Number(line.dataset.variant || 0);
  if (!variantId) return;

  const items = [{ id: variantId, quantity: 1, properties: { _i: separator() } }];

  /* If this tap is the one that earns a free garment, send the free line in
     the same request. The cart the drawer carries is enough to know, so the
     tap that matters most costs one round trip instead of two. Should the
     cart have moved elsewhere in the meantime, the top-up below still runs
     against the truth and corrects it. */
  const el = $('[data-cart-drawer]');
  const label = el ? el.dataset.freeProp : null;
  if (el && label && el.dataset.freeThird !== 'false' && el.dataset.freeLine === 'true' && lastCart) {
    const host = lastCart.items.find((i) => i.key === line.dataset.key);
    if (host) {
      const pending = Object.assign({}, host, { key: 'pending', quantity: 1, properties: { _i: 'pending' } });
      const step = nextFreeStep({ items: lastCart.items.concat([pending]) }, label, true);
      if (step && step.op === 'add') items.push({ id: step.id, quantity: 1, properties: step.properties });
    }
  }

  const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items, sections: id || undefined }),
  });
  if (!res.ok) throw new Error(`Could not add another: ${res.status}`);
  const body = await res.json();
  if (ticket !== cartTicket) return;

  let markup = body.sections && id ? body.sections[id] : null;

  /* The new row goes on screen off this one response. Everything after
     this is settling the offer, which can repaint again when it lands. */
  if (!paintDrawer(markup)) await refresh();
  if (lastCart) syncCount(lastCart.item_count);

  /* Only the last tap of a burst reconciles the offer: five fast taps used
     to reconcile five times towards states the next tap invalidated. */
  if (!lastInQueue()) return;

  /* The add response carries the drawer, and the drawer carries the cart, so
     the top-up can weigh the offer without another read. The read is only
     the fallback for markup that came back without it. */
  let cart = lastCart || (await cartAndDrawer()).cart;
  if (ticket !== cartTicket) return;

  /* This button can carry the cart across the threshold that earns a free
     garment, so it has to check like every other path that changes the cart,
     but only once the taps behind it have all landed. */
  const topped = await topUpFreeThird(cart);
  if (topped) {
    if (ticket !== cartTicket) return;
    cart = topped.cart;
  }
  syncCount(cart.item_count);
}

/* Colour and size controls on a cart line. Picking either one resolves the
   pair against that line's own variant table and swaps as soon as the
   combination exists, so a colour tap keeps the size the customer already
   chose instead of making them re-pick it. */
function initLineEditors() {
  document.addEventListener('click', async (e) => {
    const toggle = e.target.closest('[data-line-edit]');
    if (toggle) {
      const editor = toggle.parentElement.parentElement.querySelector('[data-line-editor]');
      if (!editor) return;
      const open = editor.hidden;
      editor.hidden = !open;
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      return;
    }

    const swatch = e.target.closest('[data-le-colour]');
    const size = e.target.closest('[data-le-size]');
    if (!swatch && !size) return;

    const editor = (swatch || size).closest('[data-line-editor]');
    const data = JSON.parse($('[data-le-data]', editor).textContent);

    /* Ask the payload which options this product has. A product sized only
       has no colour row, and resolving against a colour it never had would
       match nothing and leave the pencil doing nothing at all. */
    const colour = data.hasColour
      ? (swatch ? swatch.dataset.leColour : currentPick(editor, '[data-le-colour]', data.colour))
      : null;
    const wanted = data.hasSize
      ? (size ? size.dataset.leSize : currentPick(editor, '[data-le-size]', data.size))
      : null;

    paintPicks(editor, colour, wanted);

    const match = data.variants.find((v) =>
      (!data.hasColour || v.colour === colour) && (!data.hasSize || v.size === wanted));
    const note = $('[data-le-note]', editor);
    if (!match || !match.available) {
      if (note) { note.textContent = 'That size is sold out in this colour.'; note.hidden = false; }
      return;
    }
    if (note) note.hidden = true;
    if (match.id === data.variantId) return;

    await swapVariant(editor, match.id);
  });
}

function currentPick(editor, selector, fallback) {
  const on = $$(selector, editor).find((b) => b.classList.contains('is-on'));
  return on ? (on.dataset.leColour || on.dataset.leSize) : fallback;
}

function paintPicks(editor, colour, size) {
  const data = JSON.parse($('[data-le-data]', editor).textContent);
  $$('[data-le-colour]', editor).forEach((b) => {
    const on = b.dataset.leColour === colour;
    b.classList.toggle('is-on', on);
    b.setAttribute('aria-checked', on ? 'true' : 'false');
  });
  const name = $('[data-le-colour-name]', editor);
  if (name && colour) name.textContent = colour;

  $$('[data-le-size]', editor).forEach((b) => {
    const on = b.dataset.leSize === size;
    b.classList.toggle('is-on', on);
    b.setAttribute('aria-checked', on ? 'true' : 'false');
    const v = data.variants.find((x) => (!data.hasColour || x.colour === colour) && x.size === b.dataset.leSize);
    b.classList.toggle('is-out', !v || !v.available);
  });
}

function syncCount(n) {
  const count = $('[data-cart-count]');
  if (!count) return;
  count.textContent = String(n);
  count.classList.toggle('is-empty', n === 0);
}

function initCart() {
  const drawer = $('[data-cart-drawer]');
  if (!drawer) return;
  lastCart = cartFromDoc(document);

  document.addEventListener('click', async (e) => {
    if (e.target.closest('[data-cart-open]')) {
      /* Opening the cart is not a cart change, so this used to be the one path
         that never checked. A customer who reached two garments and then just
         opened the drawer saw no free one, and only adding a third and
         removing it again shook it loose, because that was a change. */
      /* On screen first. The drawer is rendered with the page and repainted
         after every change, so what it already holds is what the cart holds,
         and making the customer watch two round trips before anything appears
         is most of why opening the cart felt slow. The settle behind it
         corrects the rare case, such as a second tab. */
      openPanel(drawer);
      try {
        await inTurn(async () => {
          const { cart, markup } = await cartAndDrawer();
          const topped = await topUpFreeThird(cart);
          paintDrawer(topped ? topped.markup : markup);
          syncCount((topped ? topped.cart : cart).item_count);
        });
      } catch (err) {
        console.error(err);
      }
      return;
    }

    if (e.target.closest('[data-cart-close]')) {
      closePanel(drawer);
      return;
    }

    const up = e.target.closest('[data-qty-up]');
    const down = e.target.closest('[data-qty-down]');
    if (!up && !down) return;

    const line = e.target.closest('[data-key]');
    if (!line) return;

    const nEl = $('[data-qty-n]', line);
    const current = Number(nEl.textContent.trim());
    const next = Math.max(0, up ? current + 1 : current - 1);

    /* Move now. The response repaints the drawer a moment later with the
       authoritative rows, but a button that does nothing until the network
       answers reads as broken, and gets tapped again. Up adds a row, so a
       ghost of this one appears beneath it; down moves the number. */
    let ghost = null;
    if (up) {
      ghost = line.cloneNode(true);
      ghost.removeAttribute('data-key');
      ghost.classList.add('is-pending');
      ghost.setAttribute('aria-busy', 'true');
      line.after(ghost);
    } else {
      nEl.textContent = String(next);
      line.classList.toggle('is-going', next === 0);
    }
    e.target.closest('[data-qty]').setAttribute('aria-busy', 'true');

    try {
      /* Up adds another garment as its own row rather than raising this one's
         quantity, so every garment keeps its own pencil and can be re-specced
         on its own. Down is still a quantity change, which at one is a
         removal. */
      if (up) await addAnother(line);
      else await changeLine(line.dataset.key, next, line.dataset.i);
    } catch (err) {
      /* Put it back rather than leaving a row or a figure the cart never took. */
      if (ghost) ghost.remove();
      nEl.textContent = String(current);
      line.classList.remove('is-going');
      e.target.closest('[data-qty]').removeAttribute('aria-busy');
      console.error(err);
    }
  });

  document.addEventListener('keydown', (e) => {
    if (!drawer.hidden) trap(e, drawer);
  });

  /* Anything on the page that adds to the cart dispatches this, so the buy
     box never has to know how the drawer is built. */
  document.addEventListener('cart:changed', async (e) => {
    /* Whoever changed the cart hands over the drawer markup that came back
       with it. Painting and opening happen first, off that markup, so the
       drawer is on screen after a single request. The count and the gift
       sweep both need the whole cart, which the add response does not carry,
       so they settle behind a drawer the customer can already see. */
    const markup = e.detail && e.detail.sectionHtml;
    const painted = paintDrawer(markup);
    openPanel(drawer);
    if (e.detail && typeof e.detail.count === 'number') syncCount(e.detail.count);

    /* The cart handed over with the event describes the moment it was sent.
       If anything was already queued ahead of this, that description is out of
       date by the time the settle runs, and the top-up would weigh the offer
       against a cart that has moved. Read it again in that case. */
    const stale = cartPending > 0;

    /* The settle joins the queue; the paint and the open above do not. Those
       are local work off markup already in hand, and the drawer should be on
       screen whether or not another change is still finishing. The settle can
       top the cart up, so it has to take its turn like every other mutation:
       add on the product page and then tap plus straight away, and these two
       used to run against each other. */
    await inTurn(async () => {
      const ticket = ++cartTicket;
      let cart = stale ? null : ((e.detail && e.detail.cart) || (painted ? lastCart : null));
      if (!cart) cart = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
      if (ticket !== cartTicket) return;

      const topped = lastInQueue() ? await topUpFreeThird(cart) : null;
      if (topped) {
        if (ticket !== cartTicket) return;
        cart = topped.cart;
        if (!paintDrawer(topped.markup)) await refresh();
      }
      syncCount(cart.item_count);

      if (await reconcileGifts(cart)) {
        if (ticket !== cartTicket) return;
        const after = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
        syncCount(after.item_count);
        await refresh();
      } else if (!painted) {
        await refresh();
      }
    });
    openPanel(drawer);
  });
}

/* Generic horizontal carousels: any [data-rc-row] scrolls by one card via its
   sibling prev/next buttons. */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-rc-prev],[data-rc-next]');
  if (!btn) return;
  const scope = btn.closest('.rc, section, div');
  const row = scope && scope.parentElement
    ? (scope.querySelector('[data-rc-row]') || scope.parentElement.querySelector('[data-rc-row]'))
    : null;
  if (!row) return;
  const card = row.firstElementChild;
  const w = card ? card.getBoundingClientRect().width + 16 : row.clientWidth * 0.8;
  row.scrollBy({ left: btn.hasAttribute('data-rc-next') ? w : -w, behavior: 'smooth' });
});

/* A swipeable row that is not a scroller.

   The obvious way to build one of these is a `scroll-snap` overflow box, and
   it is the wrong way on a phone. The browser owns the gesture, so the row
   rubber-bands past its own ends, a diagonal drag pans it on both axes at
   once, and a long press hands the picture to the operating system as a drag
   item. All three read to a customer as the page coming apart under the
   finger, and none of them can be turned off from inside a scroller.

   So nothing here scrolls. The frame is `overflow: hidden`, the track is a
   flex row moved by a transform, and this element owns the gesture from
   first touch to last. `touch-action: pan-y` on the frame splits the two
   directions cleanly: the browser keeps vertical, which is the page scroll
   and must stay native, and we take horizontal, which is the swipe. The
   picture can only ever travel sideways, in whole slides, between the first
   and the last. This is the pattern the reference gallery uses.

   Settled transforms are written in percent so a resize corrects itself with
   no measuring; only the live drag works in pixels. */
function wakeDeferred(root) {
  for (const img of root.querySelectorAll('img[data-src], img[data-srcset]')) {
    if (img.dataset.srcset) {
      img.srcset = img.dataset.srcset;
      img.removeAttribute('data-srcset');
    }
    if (img.dataset.src) {
      img.src = img.dataset.src;
      img.removeAttribute('data-src');
    }
  }
}

function wakeDeferredWhenIdle() {
  const run = () => wakeDeferred(document);
  if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 2500 });
  else setTimeout(run, 400);
}
if (document.readyState === 'complete') wakeDeferredWhenIdle();
else window.addEventListener('load', wakeDeferredWhenIdle, { once: true });

class SwipeTrack extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.i = 0;
    this.moved = 0;

    this.addEventListener('pointerdown', this.onDown);
    this.addEventListener('pointermove', this.onMove);
    this.addEventListener('pointerup', this.onUp);
    this.addEventListener('pointercancel', this.onUp);
    // A drag that ends on top of a link or a button must not also click it.
    this.addEventListener('click', this.onClick, true);
    this.paint(false);
    if (this.querySelector('img[data-src], img[data-srcset]')) {
      this.addEventListener('pointerdown', () => wakeDeferred(this), { once: true });
    }
  }

  get count() { return this.children.length; }
  get index() { return this.i; }

  onDown = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    this.x0 = e.clientX;
    this.y0 = e.clientY;
    this.t0 = e.timeStamp;
    this.dx = 0;
    this.axis = null;              // unknown until the finger commits to one
    // Capture keeps the drag alive when the finger leaves the frame. Losing it
    // is not a reason to lose the swipe, so a refusal is not fatal.
    try { this.setPointerCapture(e.pointerId); } catch (err) { /* no capture */ }
  };

  onMove = (e) => {
    if (this.x0 === undefined) return;
    const dx = e.clientX - this.x0;
    const dy = e.clientY - this.y0;

    /* The first few pixels decide who owns the gesture, and the decision is
       final for the rest of it. Re-deciding mid-drag is what makes a row feel
       like it is fighting the finger. */
    if (!this.axis) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      this.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      if (this.axis === 'y') {
        try { this.releasePointerCapture(e.pointerId); } catch (err) { /* none */ }
        this.x0 = undefined;
        return;
      }
      this.classList.add('is-drag');
    }

    e.preventDefault();
    /* Past the first and the last slide the row still answers the finger, but
       at a third of the distance. A row that stops dead feels broken; one that
       follows all the way feels like it has come loose. */
    const last = this.count - 1;
    const over = (this.i === 0 && dx > 0) || (this.i === last && dx < 0);
    this.dx = over ? dx * 0.33 : dx;
    this.style.transform =
      `translate3d(calc(${-this.i * 100}% + ${this.dx - this.i * this.gap}px), 0, 0)`;
  };

  onUp = (e) => {
    if (this.x0 === undefined || this.axis !== 'x') {
      this.x0 = undefined;
      return;
    }
    const w = this.clientWidth || 1;
    const dt = Math.max(e.timeStamp - this.t0, 1);
    const speed = this.dx / dt;                 // px per ms, signed

    /* A slow drag has to cross a fifth of the frame to turn the page; a flick
       carries it on velocity alone, which is what a thumb actually does. */
    let next = this.i;
    if (Math.abs(this.dx) > w * 0.2 || Math.abs(speed) > 0.35) {
      next = this.i + (this.dx < 0 ? 1 : -1);
    }
    this.moved = Math.abs(this.dx);
    this.x0 = undefined;
    this.axis = null;
    this.classList.remove('is-drag');
    this.go(next);
  };

  onClick = (e) => {
    if (this.moved > 6) {
      e.preventDefault();
      e.stopPropagation();
      this.moved = 0;
    }
  };

  go(i, announce = true) {
    wakeDeferred(this);
    const next = Math.min(Math.max(i, 0), this.count - 1);
    const changed = next !== this.i;
    this.i = next;
    this.paint(true);
    if (announce) {
      this.dispatchEvent(new CustomEvent('swipe:change', {
        bubbles: true,
        detail: { index: this.i, changed }
      }));
    }
  }

  get gap() { return parseFloat(getComputedStyle(this).columnGap) || 0; }

  paint() {
    this.style.transform = `translate3d(calc(${-this.i * 100}% - ${this.i * this.gap}px), 0, 0)`;
  }
}

customElements.define('swipe-track', SwipeTrack);

document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-play-src]');
  if (!btn) return;
  const still = btn.querySelector('img');
  const video = document.createElement('video');
  video.className = btn.dataset.playClass || '';
  video.controls = true;
  video.playsInline = true;
  video.preload = 'auto';
  if (btn.dataset.playPoster) video.poster = btn.dataset.playPoster;
  if (still) {
    if (still.getAttribute('width')) video.setAttribute('width', still.getAttribute('width'));
    if (still.getAttribute('height')) video.setAttribute('height', still.getAttribute('height'));
  }
  video.src = btn.dataset.playSrc;
  btn.replaceWith(video);
  video.play().catch(() => {});
});

/* The last line of defence on the drag lock in theme.css. Firefox implements
   neither -webkit-user-drag nor the touch callout, so without this a picture
   still tears out of its frame there. Cancelling the event costs nothing: no
   image or link on the storefront is meant to be dragged anywhere. */
document.addEventListener('dragstart', (e) => {
  const t = e.target;
  if (t instanceof HTMLImageElement || (t.closest && t.closest('img, a, video'))) {
    e.preventDefault();
  }
});

initNav();
initCart();
initLineEditors();
initFreePicker();

export { refresh, changeLine, openPanel, closePanel };
